import React, { useCallback, useRef, useState } from 'react';
import '../../../css/common/events/EventAssignmentConfirmModal.css';
import { assignCurrentAdminToEvents } from '../../../utils/eventAssignments';
import DsButton from '../DsButton';
import DsModal from '../DsModal';

const normalizeEventUuids = (eventUuids) =>
  [...new Set(
    (Array.isArray(eventUuids) ? eventUuids : [eventUuids])
      .map((value) => String(value || '').trim())
      .filter(Boolean)
  )];

function EventAssignmentConfirmModal({
  open,
  title = '担当者登録',
  message = 'あなたはこのイベントの担当者ですか？',
  confirmLabel = 'はい、担当者に登録する',
  cancelLabel = 'いいえ',
  submitting = false,
  error = '',
  eventCount = 0,
  onConfirm,
  onCancel,
}) {
  if (!open) return null;

  return (
    <DsModal
      open={open}
      onClose={onCancel}
      title={title}
      size="sm"
      dismissible={!submitting}
      showClose={false}
      footer={(
        <>
          <DsButton
            type="button"
            variant="ghost"
            onClick={onCancel}
            disabled={submitting}
          >
            {cancelLabel}
          </DsButton>
          <DsButton
            type="button"
            onClick={onConfirm}
            disabled={submitting}
          >
            {submitting ? '登録中...' : confirmLabel}
          </DsButton>
        </>
      )}
    >
        <div className="event-assignment-confirm__icon" aria-hidden="true">
          <i className="fa-regular fa-user" />
        </div>
        <div className="event-assignment-confirm__body">
          <p className="event-assignment-confirm__message">{message}</p>
          {eventCount > 1 ? (
            <p className="event-assignment-confirm__note">
              選択中の未担当イベント {eventCount} 件にあなたを登録します。
            </p>
          ) : null}
          {error ? (
            <p className="event-assignment-confirm__error" role="alert">
              {error}
            </p>
          ) : null}
        </div>
    </DsModal>
  );
}

export const useEventAssignmentConfirmModal = () => {
  const resolverRef = useRef(null);
  const [state, setState] = useState(null);

  const resolveAndClose = useCallback((value) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setState(null);
    if (typeof resolve === 'function') {
      resolve(value);
    }
  }, []);

  const confirmAssignment = useCallback(({
    eventUuids,
    title = '担当者登録',
    message = 'あなたはこのイベントの担当者ですか？',
    confirmLabel,
    cancelLabel,
    onAssigned,
    onSkipped,
  } = {}) => {
    const ids = normalizeEventUuids(eventUuids);
    if (!ids.length) {
      return Promise.resolve({ confirmed: false, assigned: false });
    }

    if (typeof resolverRef.current === 'function') {
      resolverRef.current({ confirmed: false, assigned: false });
    }

    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({
        ids,
        title,
        message,
        confirmLabel,
        cancelLabel,
        onAssigned,
        onSkipped,
        submitting: false,
        error: '',
      });
    });
  }, []);

  const handleCancel = useCallback(() => {
    if (typeof state?.onSkipped === 'function') {
      state.onSkipped();
    }
    resolveAndClose({ confirmed: false, assigned: false });
  }, [resolveAndClose, state]);

  const handleConfirm = useCallback(async () => {
    if (!state || state.submitting) return;
    setState((prev) => (prev ? { ...prev, submitting: true, error: '' } : prev));
    try {
      const result = await assignCurrentAdminToEvents(state.ids);
      if (typeof state.onAssigned === 'function') {
        state.onAssigned(result);
      }
      resolveAndClose({ confirmed: true, assigned: true, result });
    } catch (error) {
      setState((prev) =>
        prev
          ? {
              ...prev,
              submitting: false,
              error: error?.message || '担当イベントの登録に失敗しました。',
            }
          : prev
      );
    }
  }, [resolveAndClose, state]);

  return {
    confirmAssignment,
    assignmentConfirmModal: (
      <EventAssignmentConfirmModal
        open={!!state}
        title={state?.title}
        message={state?.message}
        confirmLabel={state?.confirmLabel}
        cancelLabel={state?.cancelLabel}
        submitting={!!state?.submitting}
        error={state?.error}
        eventCount={state?.ids?.length || 0}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    ),
  };
};

export default EventAssignmentConfirmModal;
